import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, ArrowUpRight } from 'lucide-react';

/* Footer link config — keeps the JSX tidy and the structure obvious. */
const COLUMNS = [
  {
    heading: 'Product',
    links: [
      { label: 'Form Builder',  to: '/features' },
      { label: 'Templates',     to: '/templates' },
      { label: 'Integrations',  to: '/integrations' },
      { label: 'Formlio Sign',  to: '/formlio-sign' },
      { label: 'Pricing',       to: '/pricing' },
      { label: 'Verify Document', to: '/verify' },
    ],
  },
  {
    heading: 'Resources',
    links: [
      { label: 'Blog',            to: '/blog' },
      { label: 'Help Center',     to: '/help' },
      { label: 'Guides & Tutorials', to: '/guides' },
      { label: 'API Documentation', to: '/api-docs' },
      { label: 'Changelog',       to: '/changelog' },
      { label: 'Status',          to: '/status', external: true },
    ],
  },
  {
    heading: 'Company',
    links: [
      { label: 'About Us',  to: '/about' },
      { label: 'Contact',   to: '/contact' },
      { label: 'Careers',   to: '/careers' },
      { label: 'Press Kit', to: '/press' },
      { label: 'Login',     to: '/login' },
      { label: 'Sign Up Free', to: '/signup' },
    ],
  },
  {
    heading: 'Legal',
    links: [
      { label: 'Terms of Service',  to: '/terms' },
      { label: 'Privacy Policy',    to: '/privacy-policy' },
      { label: 'Security & Compliance', to: '/security' },
      { label: 'Consumer Consent Disclosure', to: '/consumer-consent' },
      { label: 'Cookies',           to: '/cookies' },
      { label: 'DPA',               to: '/dpa' },
    ],
  },
];

const SOCIAL_LINKS = [
  {
    name: 'X (Twitter)',
    href: 'https://x.com/formlioapp',
    path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
  },
  {
    name: 'Instagram',
    href: 'https://www.instagram.com/formlioapp/',
    path: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z',
  },
  {
    name: 'YouTube',
    href: 'https://www.youtube.com/@formliotech',
    path: 'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z',
  },
  {
    name: 'LinkedIn',
    href: 'https://www.linkedin.com/company/formlioapp/',
    path: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
  },
  {
    name: 'Facebook',
    href: 'https://www.facebook.com/formlioapp',
    path: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
  },
];

const FooterLink = ({ to, label, external }) => {
  const className =
    'text-sm text-slate-400 hover:text-white transition-colors inline-flex items-center gap-1.5 group';
  const content = (
    <>
      <span>{label}</span>
      {external && (
        <ArrowUpRight className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" />
      )}
    </>
  );
  if (external) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {content}
      </a>
    );
  }
  return (
    <Link
      to={to}
      className={className}
      data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {content}
    </Link>
  );
};

const Footer = () => {
  return (
    <footer
      className="bg-slate-950 text-slate-400 pt-16 lg:pt-20 pb-8 mt-16"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Main grid: brand + 4 link columns */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-10 mb-12">
          {/* Brand column — spans 2 columns on lg */}
          <div className="col-span-2 lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <img
                src="/formlio_final_logo.png"
                alt="Formlio"
                className="h-9 w-9 object-contain rounded-md"
              />
              <span className="text-xl font-bold text-white">Formlio</span>
            </div>
            <p className="text-sm leading-relaxed text-slate-400 mb-5 max-w-xs">
              Smart forms that automate your business — conditional logic, e-signatures, approvals, and 30+ integrations in one platform.
            </p>

            {/* Contact pills */}
            <ul className="space-y-2.5 text-sm">
              <li className="flex items-center gap-2.5">
                <span className="w-7 h-7 rounded-md bg-slate-800 flex items-center justify-center">
                  <Mail className="w-3.5 h-3.5 text-violet-400" />
                </span>
                <a
                  href="mailto:support@formlio.app"
                  className="text-slate-300 hover:text-white transition-colors"
                  data-testid="footer-contact-email"
                >
                  support@formlio.app
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <span className="w-7 h-7 rounded-md bg-slate-800 flex items-center justify-center">
                  <MapPin className="w-3.5 h-3.5 text-violet-400" />
                </span>
                <span className="text-slate-300">Trusted across 5 countries</span>
              </li>
            </ul>
          </div>

          {/* Link columns */}
          {COLUMNS.map((col) => (
            <div key={col.heading} data-testid={`footer-column-${col.heading.toLowerCase()}`}>
              <h4 className="text-xs font-bold tracking-[0.18em] uppercase text-white mb-4">
                {col.heading}
              </h4>
              <ul className="space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <FooterLink {...l} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-slate-800 to-transparent" />

        {/* Bottom strip */}
        <div className="pt-6 flex flex-col-reverse md:flex-row items-center md:justify-between gap-4">
          {/* Copyright + mini links */}
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-5 text-xs text-slate-500">
            <p>&copy; {new Date().getFullYear()} Formlio. All rights reserved.</p>
            <div className="hidden sm:block w-px h-3 bg-slate-700" />
            <div className="flex items-center gap-4">
              <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            </div>
          </div>

          {/* Socials */}
          <div className="flex items-center gap-2" data-testid="footer-socials">
            {SOCIAL_LINKS.map((s) => (
              <a
                key={s.name}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.name}
                title={s.name}
                className="w-9 h-9 rounded-lg bg-slate-800/80 hover:bg-indigo-600 text-slate-300 hover:text-white flex items-center justify-center transition-colors"
                data-testid={`footer-social-${s.name.toLowerCase().split(' ')[0]}`}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path d={s.path} />
                </svg>
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
